import React, { useEffect, useMemo, useRef, useState as useStateReact } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Box, Chip, Drawer, Tooltip, Typography, Avatar, Stack, Link, Button } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useSelector } from 'react-redux';

import { 
    MHidden,
    Scrollbar,
    NavSection,
    Logo,
} from '../../components';
import { renderSidebarItems } from './sidebar-config';
import { RootState, resetAllStores } from '../../store';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { useEffectiveSettings, SETTING } from '../../hooks/use-effective-settings';
import { GroupsTeamsService } from '../../services/groups-teams.service';
import { When } from 'react-if';
import { useGenericMutation } from '../../services/react-query/generic.mutation';
import { LoadingButton } from '@mui/lab';
import { handleApiError, queryClient } from '../../services/react-query/react-query.client';
import { toast } from 'react-toastify';
import { AuthService } from '../../services/auth.service';
import { publishSessionSyncEvent, SESSION_SYNC_ACTIONS } from '../../services/session-sync.service';
const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        flexShrink: 0,
        transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.complex
        }),
    },
}));

const AccountStyle = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 2.5),
    borderRadius: theme.shape.borderRadius,
}));

interface Props {
    isOpenSidebar: boolean,
    onCloseSidebar(): void,
};

const DashboardSidebar: React.FC<Props> = ({ isOpenSidebar, onCloseSidebar }) => {
    const { isCollapse, collapseClick, collapseHover, onToggleCollapse } = useCollapseDrawer();
    const { authUser } = useSelector((state: RootState) => state.auth);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { mySubscriptions } = useSelector((state: RootState) => state.groupsTeams);
    const { getSetting } = useEffectiveSettings();
    const showMessageCenter = getSetting(SETTING.showMessageCenter, true);
    const showTestMode = getSetting(SETTING.showTestMode, false);
    const showDeletionRequests = getSetting(SETTING.requireAdminApprovalForDeletion, false);
    const showRiskAssessment = getSetting(SETTING.showRiskAssessment, false);
    const showNotificationRules = getSetting(SETTING.notificationRules, false);
    const organizationType = getSetting<string>(SETTING.organizationType, 'full');

    // IRA-standalone orgs open with the sidebar collapsed (icons only) —
    // 2026-08-28 call: keep them completely focused on the assessment.
    // One-shot on settings resolve; the user can still expand it manually.
    const autoCollapsed = React.useRef(false);
    React.useEffect(() => {
        if (organizationType === 'iraStandalone' && !autoCollapsed.current) {
            autoCollapsed.current = true;
            if (!collapseClick) onToggleCollapse();
        }
    }, [organizationType, collapseClick, onToggleCollapse]);
    const teamWatchKeys = useMemo(
        () => mySubscriptions ? Array.from(mySubscriptions).filter((k) => k.startsWith('team:')) : [],
        [mySubscriptions]
    );
    const teamWatchCount = teamWatchKeys.length;

    // Build tooltip from l1Groups if available
    const { l1Groups } = useSelector((state: RootState) => state.groupsTeams);
    const tooltipText = useMemo(() => {
        if (teamWatchCount === 0) return '';
        const teamIdSet = new Set(teamWatchKeys.map((k) => k.replace('team:', '')));
        const names: string[] = [];
        (l1Groups || []).forEach((g: any) => {
            (g.teamIds || []).forEach((tid: string, _: number) => {
                if (teamIdSet.has(tid)) {
                    // Find team name from groups data
                    const teamName = (g.teams || []).find?.((t: any) => t.id === tid)?.name;
                    names.push(teamName || g.name);
                }
            });
        });
        if (names.length === 0) return `Watching ${teamWatchCount} team${teamWatchCount === 1 ? '' : 's'}`;
        if (names.length <= 3) return names.join(', ');
        return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
    }, [teamWatchKeys, l1Groups]);

    // Pulse animation when count increases
    const prevCountRef = useRef(teamWatchCount);
    const [pulse, setPulse] = useStateReact(false);
    useEffect(() => {
        if (teamWatchCount > prevCountRef.current) {
            setPulse(true);
            const timer = setTimeout(() => setPulse(false), 1500);
            return () => clearTimeout(timer);
        }
        prevCountRef.current = teamWatchCount;
    }, [teamWatchCount]);

    const { mutateAsync: handleStopMasquarade, isLoading: stopMasquaradeLoading } =
        useGenericMutation<null, boolean>(
            'stopMasquaradeUser',
            `customerWebAppUser/stopMasquarade`,
            'post'
        );
    useEffect(() => {
        if (isOpenSidebar) {
            onCloseSidebar();
        }
    }, [pathname]);

    useEffect(() => {
        if (!authUser.pomMaster) GroupsTeamsService.loadMySubscriptions();
    }, []);

    const renderContent = (
        <Scrollbar
            sx={{
                '& .simplebar-content': {
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box sx={{ px: 1.5, py: 1 }}>
                <Box component={RouterLink} to="/" sx={{ display: 'inline-flex', mb: -2 }}>
                    <Logo />
                </Box>
            </Box>
            <Stack
                spacing={1}
                sx={{
                    px: 1,
                    pb: 1,
                    ...(isCollapse && {
                        alignItems: 'center'
                    })
                }}
            >
            {isCollapse ? (
                <Avatar src={authUser.email} alt={authUser.email} />
            ) : (
                <Link underline="none" component={RouterLink} to={'/'}>
                    <AccountStyle>
                        <Box sx={{ ml: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                {authUser?.fullname} 
                            </Typography>
                            <Typography variant="body2" fontSize={12} sx={{ color: 'text.secondary' }}>
                                {authUser?.email}
                            </Typography>
                            {teamWatchCount > 0 && (
                                <Tooltip title={tooltipText} arrow placement="right">
                                    <Chip
                                        icon={<NotificationsActiveIcon sx={{ fontSize: '13px !important' }} />}
                                        label={`Watching ${teamWatchCount} team${teamWatchCount === 1 ? '' : 's'}`}
                                        size="small"
                                        clickable
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/groups-teams'); }}
                                        sx={{
                                            mt: 1.5,
                                            backgroundColor: '#e3f2fd',
                                            color: '#1565c0',
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            height: 22,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '& .MuiChip-icon': { color: '#1565c0' },
                                            '&:hover': { backgroundColor: '#bbdefb' },
                                            ...(pulse && {
                                                '@keyframes watchPulse': {
                                                    '0%': { boxShadow: '0 0 0 0 rgba(21, 101, 192, 0.5)' },
                                                    '70%': { boxShadow: '0 0 0 8px rgba(21, 101, 192, 0)' },
                                                    '100%': { boxShadow: '0 0 0 0 rgba(21, 101, 192, 0)' },
                                                },
                                                animation: 'watchPulse 0.8s ease-out 2',
                                            }),
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccountStyle>
                </Link>
            )}
            </Stack>
    
            <NavSection navConfig={renderSidebarItems(authUser, { showMessageCenter, showTestMode, showDeletionRequests, showRiskAssessment, showNotificationRules, organizationType })} isShow={!isCollapse} />
    
            <Box sx={{ flexGrow: 1 }} />

            <When condition={authUser?.masquarade}>
                <LoadingButton 
                    color='primary' 
                    loading={stopMasquaradeLoading}
                    onClick={async () => {
                        await handleStopMasquarade(null, {
                            onError: (e) => {
                                const msg = handleApiError(e);
    
                                toast(msg, {
                                    autoClose: 1500,
                                    type: 'error',
                                });
                            },
                            onSuccess: async () => {
                                // Reset all subscription-scoped stores to prevent
                                // stale data from masqueraded subscription
                                resetAllStores();
                                queryClient.clear();

                                await AuthService.whoIam();
                                publishSessionSyncEvent(SESSION_SYNC_ACTIONS.MASQUERADE_CONTEXT_CHANGED);
                                navigate('/');
    
                                toast('Successfully stopped masquerading user.', {
                                    autoClose: 1500,
                                    type: 'success',
                                });
                            },
                        });
                    }}
                >
                    You're on masquerade mode, click here to stop
                </LoadingButton>
            </When>

        </Scrollbar>
    );

    return (
        <RootStyle
            sx={{
                border: 'none',
                width: {
                    lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
                },
                ...(collapseClick && {
                    position: 'absolute'
                })
            }}
        >
            
            <MHidden width="lgUp">
                <Drawer
                    open={isOpenSidebar}
                    onClose={onCloseSidebar}
                    PaperProps={{
                        sx: { width: DRAWER_WIDTH }
                    }}
                >
                   
                    {renderContent}
                </Drawer>
            </MHidden>
    
            <MHidden width="lgDown">
                <Drawer
                    open
                    variant="persistent"
                    PaperProps={{
                        sx: {
                            width: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
                            bgcolor: 'background.default',
                            ...(collapseHover && {
                                borderRight: 0,
                                backdropFilter: 'blur(6px)',
                                WebkitBackdropFilter: 'blur(6px)',
                                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
                            })
                        }
                    }}
                >
                    {renderContent}
                </Drawer>
            </MHidden>
        </RootStyle>
    );
}

export default DashboardSidebar;
